import { css } from '@emotion/core'
import styled from '@emotion/styled'
import { transparentize } from 'polished'

import { BorderRadius } from '../../enums/borderRadius'
import { Color } from '../../enums/color'
import { Spacing } from '../../enums/spacing'
import { getDefaultTransition } from '../../utils/getDefaultTransition'
import { getShadow } from '../../utils/getShadow'
import { rwd } from '../../utils/rwd'
import { zIndex } from '../../utils/zIndex'
import {
  DRAWER_COMPACT_WIDTH,
  DRAWER_EXPANDED_LEFT_OFFSET_CSS_VAR,
  DRAWER_OFFSET_CSS_VAR,
} from './constants/drawerOffsetCssVars'
import { Mode } from './types/mode'

interface DrawerWrapperProps {
  isOpen?: boolean
  mode: Mode
}

const openedStyles = ({ isOpen }: { isOpen?: boolean }) => css`
  width: ${DRAWER_COMPACT_WIDTH};
  border-top-left-radius: ${BorderRadius.R16};
  border-bottom-left-radius: ${BorderRadius.R16};
  top: 0;
  right: 0;
  bottom: 0;
  transform: translateX(${isOpen ? 0 : '100%'});
  /* may be moved to the list of set shadows in the future */
  box-shadow: -8px 2px 20px 0 ${transparentize(0.84, Color.Grey950)};
`

const compactStyles = ({ isOpen }: { isOpen?: boolean }) => css`
  width: ${DRAWER_COMPACT_WIDTH};
  border-top-left-radius: ${BorderRadius.R16};
  border-top-right-radius: ${BorderRadius.R0};
  border-bottom-right-radius: ${BorderRadius.R0};
  border-bottom-left-radius: ${BorderRadius.R16};
  top: ${Spacing.S8};
  right: 0;
  bottom: ${Spacing.S8};
  transform: translateX(${isOpen ? 0 : '100%'});
  ${getShadow('raisedStrong')};

  ${rwd.greaterThan('xxxlarge')} {
    border-left: 1px solid ${Color.Grey100};
    top: 0;
    bottom: 0;
    box-shadow: none;
  }
`

interface ExpandedStylesProps {
  isOpen?: boolean
}

const expandedStyles = ({ isOpen }: ExpandedStylesProps) => css`
  width: calc(100vw - var(${DRAWER_EXPANDED_LEFT_OFFSET_CSS_VAR}, 0px));
  border-radius: ${BorderRadius.R0};
  top: 0;
  right: 0;
  bottom: 0;
  transform: translateX(${isOpen ? 0 : '100%'});
  box-shadow: none;
`

const getModeStyle = ({ mode, isOpen }: DrawerWrapperProps) => {
  if (mode === Mode.Expanded) {
    return expandedStyles({ isOpen })
  }
  if (mode === Mode.Compact) {
    return compactStyles({ isOpen })
  }
  return openedStyles({ isOpen })
}

export const DrawerWrapper = styled.div<DrawerWrapperProps>`
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: ${zIndex.Drawer};
  background-color: ${Color.White};
  will-change: transform, width;
  ${getDefaultTransition('transform', 'top', 'right', 'bottom', 'width', 'box-shadow', 'border-radius')};

  ${getModeStyle}

  ${({ mode }) =>
    (mode === Mode.Compact || mode === Mode.Expanded) &&
    css`
      overflow: hidden;
    `}
`

export const DrawerSqueezeZoneWrapper = styled.div`
  position: absolute;
  top: 0;
  right: var(${DRAWER_OFFSET_CSS_VAR}, 0px);
  bottom: 0;
  left: 0;
  ${getDefaultTransition('right')};
`
